import { useState } from "react";
import { formatDisplayDate, formatYen } from "@carry/shared";
import { Button } from "@carry/ui";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import { MonthStrip } from "../components/MonthStrip";
import { ReportCard } from "../components/ReportCard";
import {
  deleteDriverExpense,
  useDriverExpenses,
  type DriverExpense,
  type DriverFetcher,
} from "../hooks";
import { expenseTypeLabel, formatMonthLabel, jstMonth, monthRange } from "../lib/normalize";

export function ExpensesListScreen({
  fetcher,
  onBack,
  onAdd,
  onEdit,
  onChanged,
  successMessage,
}: {
  fetcher: DriverFetcher;
  onBack: () => void;
  onAdd: () => void;
  onEdit?: (expense: DriverExpense) => void;
  onChanged?: () => void | Promise<void>;
  successMessage?: string | null;
}) {
  const [month, setMonth] = useState(jstMonth());
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [mutationSuccess, setMutationSuccess] = useState<string | null>(successMessage ?? null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { from, to } = monthRange(month);
  const expenses = useDriverExpenses({
    apiBaseUrl: fetcher.apiBaseUrl,
    session: fetcher.session,
    fetchImpl: fetcher.fetchImpl,
    from,
    to,
  });
  const total = (expenses.expenses ?? []).reduce((a, e) => a + e.amount, 0);

  const handleDelete = async (expense: DriverExpense) => {
    const target = expense.description?.trim() || expenseTypeLabel(expense.expense_type);
    if (!confirm(`${target}（${formatYen(expense.amount)}）を削除しますか？`)) return;
    setDeletingId(expense.public_id);
    setMutationError(null);
    setMutationSuccess(null);
    try {
      await deleteDriverExpense(fetcher, expense.public_id);
      await expenses.refetch();
      setMutationSuccess("経費を削除しました");
      await onChanged?.();
    } catch (error) {
      setMutationError(error instanceof Error ? error.message : String(error));
    } finally {
      setDeletingId(null);
    }
  };
  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header title="経費履歴" sub={formatMonthLabel(month)} showBack onBack={onBack} />
      <MonthStrip value={month} onChange={setMonth} />
      <div className="px-3 py-3.5">
        <ReportCard>
          <div className="flex items-center justify-between">
            <span className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">合計</span>
            <span className="num text-[length:var(--fs-lg)] font-semibold">{formatYen(total)}</span>
          </div>
        </ReportCard>
        <div className="mt-3.5">
          {expenses.loading && (
            <div className="p-4 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
              読込中…
            </div>
          )}
          {expenses.error && (
            <div className="rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
              ⚠ {expenses.error}
            </div>
          )}
          {mutationError && (
            <div className="mb-2 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
              <span aria-hidden="true">⚠ </span>
              <span>{mutationError}</span>
            </div>
          )}
          {mutationSuccess && (
            <div className="mb-2 rounded-lg bg-[var(--st-done-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-done-ink)]">
              ✓ {mutationSuccess}
            </div>
          )}
          {expenses.expenses && expenses.expenses.length === 0 && (
            <div className="p-4 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
              履歴はありません
            </div>
          )}
          {expenses.expenses?.map((e) => (
            <div
              key={e.public_id}
              className="mb-1.5 flex items-center justify-between rounded-lg bg-[var(--bg-raised)] px-3.5 py-2.5 shadow-sm"
            >
              <div>
                <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                  {formatDisplayDate(e.incurred_on)} · {expenseTypeLabel(e.expense_type)}
                  {/* #616: 事務が ETC を補完した分は本人が二重入力しないよう明示する。 */}
                  {e.entered_by_proxy && " · 事務入力"}
                </div>
                <div className="text-[length:var(--fs-sm)]">{e.description ?? "—"}</div>
                {/* #616: どの配達で使った経費かを一覧でも見せる (請求・支払の単位)。 */}
                {e.stop_name && (
                  <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                    🚚 {e.stop_name}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="num text-[length:var(--fs-md)] font-semibold">
                  {formatYen(e.amount)}
                </div>
                {/*
                  #616: 締め (日報承認) 前なら過去日も修正できる。承認済みかは日ごとに変わるので、
                  一覧では出し分けず API の 409 メッセージで理由を返す。
                */}
                <div className="flex gap-1">
                  <Button type="button" variant="outline" onClick={() => onEdit?.(e)}>
                    変更
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={deletingId === e.public_id}
                    onClick={() => void handleDelete(e)}
                  >
                    削除
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <BottomBar>
        <Button
          type="button"
          variant="outline"
          aria-label="配送一覧へ戻る"
          onClick={onBack}
          className="h-auto min-h-[var(--row-h)] px-3.5 py-3 text-[length:var(--fs-sm)] text-[var(--ink-3)]"
        >
          戻る
        </Button>
        <Button
          type="button"
          onClick={onAdd}
          className="h-auto min-h-[var(--row-h)] flex-1 bg-[var(--ok)] px-4 py-3 text-[length:var(--fs-md)] font-semibold text-white hover:bg-[var(--ok)]/90"
        >
          ＋ 経費を追加
        </Button>
      </BottomBar>
    </div>
  );
}
